import type { FieldProps } from '@rjsf/utils';
import type React from 'react';
import { useState, useEffect } from 'react';
import { Checkbox, Divider } from 'semantic-ui-react';

/**
 * Custom field component for nullable objects with enable/disable checkbox.
 * Allows users to independently enable or disable optional object fields.
 * When disabled, the value is set to null.
 *
 * Note that, by default, these fields are disabled when their parent is
 * enabled to provide a better UX (users can selectively enable only the
 * sub-objects they want to configure).
 */
export const NullableObjectField = (props: FieldProps) => {
  const { uiSchema, formData, onChange, disabled, readonly, name, registry } =
    props;

  // Get the default enabled state from UI schema options
  const defaultEnabled = uiSchema?.['ui:options']?.defaultEnabled !== false;

  // State for whether this object is enabled (not null)
  const [isEnabled, setIsEnabled] = useState<boolean>(() => {
    // If there's existing data, it's enabled
    if (formData !== null && formData !== undefined) {
      return true;
    }
    // Otherwise use the default (true for better UX)
    return defaultEnabled;
  });

  // Update enabled state when formData changes externally
  useEffect(() => {
    const hasData = formData !== null && formData !== undefined;
    setIsEnabled(hasData || (formData === undefined && defaultEnabled));
  }, [formData, defaultEnabled]);

  // Initialize with empty object when component mounts if enabled by default
  useEffect(() => {
    if (defaultEnabled && formData === undefined) {
      // Initialize with empty object for default enabled state
      onChange({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle checkbox toggle
  const handleToggle = (e: React.FormEvent, data: any) => {
    const newEnabled = data.checked;
    setIsEnabled(newEnabled);

    if (!newEnabled) {
      // Set to null when disabled
      onChange(null);
    } else {
      // Initialize with empty object when enabled
      onChange({});
    }
  };

  // Get the ObjectField from registry to render the object's fields
  const { fields } = registry;
  const { ObjectField } = fields;

  // Format the label for the checkbox
  const label = name ? name.charAt(0).toUpperCase() + name.slice(1) : 'Enable';

  return (
    <div className="rjsf-nullable-object-field">
      <div className="ui form">
        <div className="field" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Checkbox
              checked={isEnabled}
              onChange={handleToggle}
              disabled={disabled || readonly}
            />
            <h4 className="ui header" style={{ margin: 0 }}>
              {label}
            </h4>
          </div>
        </div>
        {isEnabled && (
          <div className="rjsf-nullable-object-content">
            <ObjectField
              {...props}
              formData={formData || {}}
              onChange={onChange}
            />
          </div>
        )}
      </div>
      <Divider />
    </div>
  );
};
